<template>
  <div class="app">
    <AppHeader class="py-0" fixed>
      <SidebarToggler class="d-lg-none" display="md" mobile />
      <SidebarToggler class="d-md-down-none" display="lg" />
      <b-link id="navbar-brand" class="navbar-brand" to="/">
        <img class="navbar-brand-full" src="img/logo-white-wide.svg" width="125" height="55" alt="HIPAAMATE" />
        <img class="navbar-brand-minimized" src="img/logo-white-wide.svg" width="125" height="55" alt="HIPAAMATE" />
      </b-link>
      <h5 class="navbar-title d-md-down-none pl-2 pt-2">{{title}}</h5>
      <b-navbar-nav class="ml-auto mr-2">
        <b-nav-item>
          <b-button id="navbar-support" type="button" variant="link" class="nav-link mr-2" @click="dlgSupport = true">
            <i class="icon-support pr-1" title="Support"></i>
            <span class="d-md-down-none">Support</span>
          </b-button>
        </b-nav-item>
        <DropdownAccount />
      </b-navbar-nav>
    </AppHeader>
    <div class="app-body">
      <AppSidebar fixed>
        <SidebarHeader>
          <h5 v-if="claims && claims.impersonate" class="navbar-title d-md-down-none pl-2 pt-2 text-danger">IMPERSONATING USER</h5>
        </SidebarHeader>
        <SidebarForm />
        <SidebarNav id="sidebar-nav" :navItems="nav"></SidebarNav>
        <SidebarFooter />
      </AppSidebar>
      <main class="main pt-3">
        <div class="container-fluid">
          <hm-progress />
          <router-view v-show="showPage" />
        </div>
        <hm-footer />
      </main>
    </div>

    <!-- Support Modal Dialog -->
    <b-form @submit.prevent="onSubmitSupport">
      <b-modal id="dlgSupport" class="modal-primary" hide-header-close v-model="dlgSupport" @shown="onShowSupport">
        <div class="font-weight-bold" slot="modal-title">
          <i class="icon-support pr-2"></i>HIPAAMATE Support
        </div>
        <div v-if="hasVideo">
          <vimeo-player ref="player" class="pb-3" :video-id="videoId" :autoplay="true" :options="videoOptions"></vimeo-player>
        </div>
        <hm-alert />
        <b-input-group class="mb-3">
          <b-form-textarea
            no-resize
            :rows="3"
            name="dlgSupportMsg"
            class="form-control"
            :placeholder="supportPlaceholder"
            v-model="dlgSupportMsg"
            v-validate="'required'"
            :disabled="loading"
            :class="{ 'is-invalid': errors.has('dlgSupportMsg') && dlgSupportSubmitted }"
          />
          <b-form-invalid-feedback id="dlgSupportMsgFeedback">Please tell us how we can help</b-form-invalid-feedback>
        </b-input-group>
        <p>
          We’ll send a response to
          <strong>{{user}}</strong>. If you would like it sent to a different email, please include the address in the message above.
        </p>
        <div slot="modal-footer">
          <b-button variant="default" :disabled="loading" @click="dlgSupport = false">Cancel</b-button>
          <b-button type="submit" variant="primary" class="ml-2" :disabled="loading" @click="onSubmitSupport">
            <i class="fa fa-refresh fa-spin" v-if="loading"></i> Submit
          </b-button>
        </div>
      </b-modal>
    </b-form>

    <!-- Support Confirmation Modal Dialog -->
    <b-modal id="dlgSupportConfirm" class="modal-primary" hide-header-close v-model="dlgSupportConfirm">
      <div class="font-weight-bold" slot="modal-title">
        <i class="icon-paper-plane pr-2"></i>Submission Successful
      </div>
      <p>We will do our best to answer your question. Expect an email or phone call within 24 hours (likely much sooner).</p>
      <p>
        Thank you,
        <br />The HIPAAMATE Team
      </p>
      <div slot="modal-footer">
        <b-button variant="primary" @click="dlgSupportConfirm = false">OK</b-button>
      </div>
    </b-modal>

    <!-- Idle Modal Dialog -->
    <hm-idle-dlg />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { Header as AppHeader, SidebarToggler, Sidebar as AppSidebar, SidebarFooter, SidebarForm, SidebarHeader, SidebarMinimizer, SidebarNav, Aside as AppAside, AsideToggler, Footer as TheFooter, Breadcrumb } from '@coreui/vue'
import nav from '@/navbar'
import DropdownAccount from './DropdownAccount'
import { authService } from '@/services';

export default {
  name: 'DefaultContainer',
  components: {
    AsideToggler,
    AppHeader,
    AppSidebar,
    AppAside,
    Breadcrumb,
    SidebarForm,
    SidebarFooter,
    SidebarToggler,
    SidebarHeader,
    SidebarNav,
    SidebarMinimizer,
    TheFooter,
    DropdownAccount,
  },
  data() {
    return {
      nav: nav.items,
      dlgSupport: false,
      dlgSupportMsg: '',
      dlgSupportSubmitted: false,
      dlgSupportConfirm: false,
      videoOptions: { responsive: true }
    }
  },
  computed: {
    ...mapState('user', ['user', 'claims']),
    ...mapGetters({
      loading: 'isLoading',
      missingFlags: 'progress/missingFlags'
    }),
    title() {
      return this.$route.meta && this.$route.meta.title ? this.$route.meta.title : '';
    },
    showPage() {
      return (!this.missingFlags || this.missingFlags.length == 0);
    },
    hasVideo() {
      return this.$route.meta && this.$route.meta.videoId;
    },
    videoId() {
      return this.hasVideo ? this.$route.meta.videoId : '';
    },
    supportPlaceholder() {
      return this.hasVideo ? 'Still have questions? Contact support here.' : 'How can we help you today?';
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertException: 'alert/exception',
    }),
    onShowSupport() {
      this.alertClear();
      this.dlgSupportMsg = '';
      this.dlgSupportSubmitted = false;
    },
    onSubmitSupport() {
      this.alertClear();
      this.dlgSupportSubmitted = true;

      this.$validator.validate()
        .then(valid => {
          if (this.dlgSupportMsg && this.dlgSupportMsg.length > 0) {
            return authService.submitFeedback(this.dlgSupportMsg, this.user, this.$route)
              .then(response => {
                this.dlgSupport = false;
                this.dlgSupportConfirm = true;
              }, err => {
                this.alertException({ err, message: 'Failed to submit feedback, please try again later.' });
              });
          }
        });
    }
  }
}
</script>

<style scoped>
.navbar-title {
  color: #fff;
  font-weight: bold;
}
</style>